import { useEffect, useMemo, useState } from "react";
import { StudentList } from "../components/StudentList";
import { PendingStudentsList } from "../components/PendingStudentsList";
import { SubjectTree } from "../components/SubjectTree";
import StudentFilter from "types/StudentFilter";
import Student, { GradeLevelGroup } from "types/Student";
import { LearningPanel } from "components/LearningPanel";
import { useStudents } from "context/StudentsContext";
import { buildGradeLevelHierarchy } from "lib/students";
import Spinner from "components/ui/Spinner";
import { fetchTeacherSubjects, mergeGradeLevelGroups } from "lib/students";
import { Users, X } from "lucide-react";

interface HomeProps {
  onStudentListToggle?: (isOpen: boolean) => void;
}

export default function Home({ onStudentListToggle }: HomeProps) {
  const [filter, setFilter] = useState<StudentFilter>({});
  const [isStudentListOpen, setIsStudentListOpen] = useState(false);
  const {
    students,
    pendingStudents,
    loading,
    error,
    refresh,
    selectedStudentId,
    setSelectedStudentId,
  } = useStudents();

  // Notify parent component when student list state changes
  useEffect(() => {
    onStudentListToggle?.(isStudentListOpen);
  }, [isStudentListOpen, onStudentListToggle]);

  const filteredStudents = useMemo(() => {
    return !!students
      ? students.filter((student) => {
          if (filter.subject)
            return student.subjects.some((s) => s.subject === filter.subject);
          if (filter.gradeLevel)
            return student.subjects.some(
              (s) => s.gradeLevel === filter.gradeLevel
            );
          return true;
        })
      : [];
  }, [filter, students]);

  const allStudents = useMemo(() => {
    return [...students, ...pendingStudents];
  }, [students, pendingStudents]);

  const filteredAllStudents = useMemo(() => {
    return !!allStudents
      ? allStudents.filter((student) => {
          if (filter.subject)
            return student.subjects.some((s) => s.subject === filter.subject);
          if (filter.gradeLevel)
            return student.subjects.some(
              (s) => s.gradeLevel === filter.gradeLevel
            );
          return true;
        })
      : [];
  }, [filter, allStudents]);

  const gradeLevelGroups: GradeLevelGroup[] = useMemo(() => {
    return buildGradeLevelHierarchy(students);
  }, [students]);

  const [mergedGroups, setMergedGroups] =
    useState<GradeLevelGroup[]>(gradeLevelGroups);

  useEffect(() => {
    const fetchAndMergeSubjects = async () => {
      const teacherGroups = await fetchTeacherSubjects();
      const merged = mergeGradeLevelGroups(gradeLevelGroups, teacherGroups);
      setMergedGroups(merged);
    };

    fetchAndMergeSubjects();
  }, [gradeLevelGroups]);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  const student: Student | null = selectedStudentId
    ? allStudents.find((s) => s.id === selectedStudentId) || null
    : null;

  const handleFilterChange = (newFilter: StudentFilter) => {
    setFilter(newFilter);
    setSelectedStudentId(null);
  };

  return (
    <div className="flex flex-1 relative">
      {/* Mobile student list toggle button */}
      <button
        onClick={() => setIsStudentListOpen(!isStudentListOpen)}
        onTouchEnd={(e) => {
          // Prevent default to avoid double-tap zoom and ensure click fires
          e.preventDefault();
          setIsStudentListOpen(!isStudentListOpen);
        }}
        className="lg:hidden fixed top-4 right-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-gray-200"
      >
        {isStudentListOpen ? (
          <X className="w-5 h-5 text-gray-600" />
        ) : (
          <Users className="w-5 h-5 text-gray-600" />
        )}
      </button>

      {/* Mobile overlay for student list */}
      {isStudentListOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsStudentListOpen(false)}
        />
      )}

      {/* Student List Panel */}
      <div
        className={`fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isStudentListOpen ? "translate-x-0" : "-translate-x-full"
        } w-80 lg:w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden`}
      >
        <div className="p-4 overflow-y-auto flex-1">
          <SubjectTree
            gradeLevelGroups={mergedGroups}
            onSelect={handleFilterChange}
          />
          <StudentList
            filter={filter}
            students={filteredStudents}
            selectedStudentId={selectedStudentId}
            onSelectStudent={(id) => {
              setSelectedStudentId(id);
              // Add a small delay to ensure the state update completes before closing the panel
              setTimeout(() => setIsStudentListOpen(false), 50);
            }}
            onStudentAdded={refresh}
            gradeLevelGroups={mergedGroups}
          />
          <PendingStudentsList
            students={pendingStudents}
            selectedStudentId={selectedStudentId}
            onSelectStudent={(id) => {
              setSelectedStudentId(id);
              // Add a small delay to ensure the state update completes before closing the panel
              setTimeout(() => setIsStudentListOpen(false), 50);
            }}
            onStudentAction={refresh}
            gradeLevelGroups={mergedGroups}
          />
        </div>
      </div>

      {/* Main Content Panel */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="h-full p-4 space-y-4 overflow-y-auto">
          <LearningPanel
            student={student}
            filter={filter}
            students={filteredAllStudents}
          />
        </div>
      </div>
    </div>
  );
}
